import {
  Typography,
  Box,
  Card,
  Grid,
  Avatar,
  Autocomplete,
  TextField,
  InputAdornment,
  IconButton,
  Container,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getTutors, clearStudent } from "../../state/slices/studnent";
import Loading from "../../components/loading/loading";
import { Search, Person, Phone, Email, Schedule, Quiz, Collections, TrendingUp } from "@mui/icons-material";
import dayjs from "dayjs";

const StudentClassRoom = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { tutors, loading, error, success } = useSelector(
    (state) => state.student
  );
  const dispatch = useDispatch();
  const [searchTutor, setSearchTutor] = React.useState([]);

  const handleTutorsFetch = () => {
    dispatch(getTutors());
  };

  useEffect(() => {
    handleTutorsFetch();
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setSearchTutor(tutors);
      dispatch(clearStudent());
    }
  }, [success, tutors, dispatch]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 3,
            textAlign: 'center',
          }}
        >
          {t('classRoom.studentClassRoom')}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card
              onClick={() => navigate("/quiz/student")}
              sx={{
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }
              }}
            >
              <Quiz sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>{t('classRoom.quizzes')}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              onClick={() => navigate("/flashcard/student")}
              sx={{
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                '&:hover': {
                  borderColor: theme.palette.secondary.main,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                }
              }}
            >
              <Collections sx={{ fontSize: 40, color: theme.palette.secondary.main, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>{t('classRoom.flashcards')}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              onClick={() => navigate("/progress")}
              sx={{
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
                '&:hover': {
                  borderColor: theme.palette.success.main,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                }
              }}
            >
              <TrendingUp sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>{t('classRoom.progress')}</Typography>
            </Card>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <Autocomplete
            onChange={(event, newValue) => {
              if (newValue === null) {
                setSearchTutor(tutors);
              } else {
                const newTutor = tutors.filter(
                  (tutor) =>
                    tutor.tutor.firstName + " " + tutor.tutor.lastName === newValue
                );
                setSearchTutor(newTutor);
              }
            }}
            disablePortal
            options={tutors.map(
              (tutor) => tutor.tutor.firstName + " " + tutor.tutor.lastName
            )}
            sx={{
              width: { xs: '100%', sm: 500 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: 'white',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  }
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('classRoom.tutorName')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton>
                        <Search sx={{ color: theme.palette.primary.main }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Loading />
          </Box>
        ) : searchTutor.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                mb: 1,
              }}
            >
              {t('classRoom.noTutorFound')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('classRoom.noTutorsAvailable')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {searchTutor.map((tutor) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tutor.id}>
                <Card
                  onClick={() => {
                    navigate("/student-detail-class-room", { state: { tutor } });
                  }}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Avatar
                      alt={tutor.tutor.firstName + " " + tutor.tutor.lastName}
                      src={tutor.tutor.profileImage}
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 2,
                        border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    >
                      {tutor.tutor.firstName + " " + tutor.tutor.lastName}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {tutor.tutor.phoneNumber}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email sx={{ color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {tutor.tutor.email}
                        </Typography>
                      </Box>
                      {tutor.upcomingSession && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Schedule sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              {t('classRoom.upcomingClass')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                              {dayjs(tutor.upcomingSession.startingTime).format("DD/MM/YYYY hh:mm a")} - {dayjs(tutor.upcomingSession.endingTime).format("hh:mm a")}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {!tutor.upcomingSession && (
                        <Chip
                          label={t('classRoom.noUpcomingClass')}
                          size="small"
                          color="default"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default StudentClassRoom;
